import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProperties, createProperty, deleteProperty, updateProperty, manualCheckout, getMyCleaningCompany, listMaintenance, createPropertyTask, getMe } from '../lib/api';
import { cacheGet, cacheSet } from '../lib/cache';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import PullToRefresh from '../components/PullToRefresh';
import { getUser } from '../lib/auth';
import { toast } from 'sonner';
import { Plus, Eye, Trash2, Search, Pencil, X, User as UserIcon, CheckCircle2, Bed, Minus, Upload, Shield, Clock, Bell, MapPin, Filter, SlidersHorizontal, RotateCcw, Hand, Wrench, QrCode, LogOut } from 'lucide-react';
import MobileNav, { PropertiesIcon } from '../components/MobileNav';
import BulkImportModal from '../components/BulkImportModal';
import MaintenanceModal from '../components/MaintenanceModal';
import CheckoutInstructionsPicker, { normalizeInstructions } from '../components/CheckoutInstructionsPicker';
import PropertyTasksEditor from '../components/PropertyTasksEditor';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

// Bed types configuration
const BED_TYPES = ['Single', 'Double', 'Small double', 'Queen', 'King', 'Super King'];

// Average cleaning time options (minutes) shown as H:MM, from 0:15 up to 5:00.
const CLEANING_TIME_OPTIONS = [
  { value: '', label: 'Not set' },
  ...Array.from({ length: 20 }, (_, i) => {
    const mins = (i + 1) * 15;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return { value: mins, label: `${h}:${m.toString().padStart(2, '0')}` };
  }),
];

// Default bedroom with all bed types at 0
const formatCleaningTime = (mins) => {
  if (!mins) return null;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m} min`;
};

const FilterChip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`px-3.5 py-1.5 rounded-full text-sm font-medium border whitespace-nowrap flex-shrink-0 transition-colors ${
      active
        ? 'bg-gradient-to-r from-blue-500 to-emerald-500 text-white border-transparent'
        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
    }`}
  >
    {children}
  </button>
);

const ActiveFilterPill = ({ label, onRemove, testid }) => (
  <button
    type="button"
    onClick={onRemove}
    data-testid={testid}
    className="inline-flex items-center gap-1.5 h-11 pl-3.5 pr-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-sm font-medium text-slate-700 transition-colors"
  >
    <span className="max-w-[120px] truncate">{label}</span>
    <X size={14} className="text-slate-500" />
  </button>
);

const createDefaultBedroom = () => ({
  beds: BED_TYPES.reduce((acc, type) => ({ ...acc, [type]: 0 }), {})
});

export default function DashboardPage() {
  const [properties, setProperties] = useState(cacheGet('properties') || []);
  const [loading, setLoading] = useState(!cacheGet('properties'));
  const [searchQuery, setSearchQuery] = useState('');
  const [cityFilter, setCityFilter] = useState('all');
  const [staffFilter, setStaffFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [newPropertyName, setNewPropertyName] = useState('');
  const [newPropertyAddress, setNewPropertyAddress] = useState('');
  const [newPropertyCity, setNewPropertyCity] = useState('');
  const [bedrooms, setBedrooms] = useState([createDefaultBedroom()]);
  const [checkoutInstructions, setCheckoutInstructions] = useState([]);
  const [newTasks, setNewTasks] = useState([]);
  const [editCheckoutInstructions, setEditCheckoutInstructions] = useState([]);
  const [creating, setCreating] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [editName, setEditName] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [editCity, setEditCity] = useState('');
  const [editBedrooms, setEditBedrooms] = useState([]);
  const [saving, setSaving] = useState(false);
  const [checkingOutId, setCheckingOutId] = useState(null);
  const [confirmCheckout, setConfirmCheckout] = useState(null);
  const [newAvgCleaningTime, setNewAvgCleaningTime] = useState('');
  const [editAvgCleaningTime, setEditAvgCleaningTime] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const navigate = useNavigate();
  const user = getUser();
  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'company_manager';
  const [myCleanerIds, setMyCleanerIds] = useState(new Set());
  const [showMaintenance, setShowMaintenance] = useState(false);
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const [maintenanceOpen, setMaintenanceOpen] = useState(0);
  const [tasksEnabled, setTasksEnabled] = useState(true);

  const loadMaintenanceMeta = async () => {
    if (!(isAdmin || isManager)) return;
    try {
      const res = await listMaintenance({ status: 'all' });
      setMaintenanceEnabled(true);
      setMaintenanceOpen(res.data.counts.open);
    } catch (e) {
      if (e.response?.status === 403) setMaintenanceEnabled(false);
    }
  };

  const loadProperties = async (silent = false) => {
    try {
      const response = await getProperties();
      setProperties(response.data.properties);
      cacheSet('properties', response.data.properties);
    } catch (error) {
      if (!silent) toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProperties();
    loadMaintenanceMeta();
    getMe().then((r) => setTasksEnabled(r.data?.tasks_enabled !== false)).catch(() => {});
    if (user?.role === 'company_manager') {
      getMyCleaningCompany()
        .then((res) => setMyCleanerIds(new Set((res.data.cleaners || []).map((c) => c.id))))
        .catch(() => {});
    }
  }, []);

  useAutoRefresh(() => { loadProperties(true); loadMaintenanceMeta(); });

  const cities = useMemo(() => {
    const set = new Set();
    properties.forEach((p) => { if (p.city && p.city.trim()) set.add(p.city.trim()); });
    return Array.from(set).sort();
  }, [properties]);

  const staffOptions = useMemo(() => {
    const map = new Map();
    properties.forEach((p) => (p.assignedUsers || []).forEach((u) => { if (!map.has(u.id)) map.set(u.id, u); }));
    return Array.from(map.values()).sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email));
  }, [properties]);

  // Filter properties by search + city + staff + status
  const filteredProperties = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return properties.filter((property) => {
      if (query && !(
        property.name.toLowerCase().includes(query) ||
        (property.address && property.address.toLowerCase().includes(query)) ||
        (property.city && property.city.toLowerCase().includes(query))
      )) return false;
      if (cityFilter !== 'all' && (property.city || '') !== cityFilter) return false;
      if (statusFilter === 'active' && !property.isActive) return false;
      if (statusFilter === 'inactive' && property.isActive) return false;
      if (staffFilter !== 'all' && !(property.assignedUsers || []).some((u) => u.id === staffFilter)) return false;
      return true;
    });
  }, [properties, searchQuery, cityFilter, staffFilter, statusFilter]);

  const activeFilterCount = (cityFilter !== 'all' ? 1 : 0) + (staffFilter !== 'all' ? 1 : 0) + (statusFilter !== 'all' ? 1 : 0);
  const resetFilters = () => { setCityFilter('all'); setStaffFilter('all'); setStatusFilter('all'); };

  const handleCreateProperty = async (e) => {
    e.preventDefault();
    if (!newPropertyName.trim()) return;

    setCreating(true);
    try {
      const res = await createProperty(newPropertyName, newPropertyAddress, newPropertyCity, bedrooms, newAvgCleaningTime === '' ? null : Number(newAvgCleaningTime), checkoutInstructions);
      const newId = res?.data?.property?.id || res?.data?.id;
      if (newId && newTasks.length > 0) {
        for (const t of newTasks) {
          try {
            await createPropertyTask(newId, { title: t.title, type: t.type, date: t.date, time: t.time, frequency: t.frequency, endDate: t.endDate });
          } catch (e) { /* skip a failed task */ }
        }
      }
      toast.success('Property created successfully');
      setNewPropertyName('');
      setNewPropertyAddress('');
      setNewPropertyCity('');
      setNewAvgCleaningTime('');
      setBedrooms([createDefaultBedroom()]);
      setCheckoutInstructions([]);
      setNewTasks([]);
      setShowCreateForm(false);
      loadProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create property');
    } finally {
      setCreating(false);
    }
  };

  // Bedroom management functions
  const addBedroom = () => {
    setBedrooms([...bedrooms, createDefaultBedroom()]);
  };

  const removeBedroom = (index) => {
    if (bedrooms.length > 1) {
      setBedrooms(bedrooms.filter((_, i) => i !== index));
    }
  };

  const updateBedCount = (bedroomIndex, bedType, delta) => {
    setBedrooms(bedrooms.map((bedroom, i) => {
      if (i === bedroomIndex) {
        const newCount = Math.max(0, (bedroom.beds[bedType] || 0) + delta);
        return { ...bedroom, beds: { ...bedroom.beds, [bedType]: newCount } };
      }
      return bedroom;
    }));
  };

  // Edit bedroom functions
  const addEditBedroom = () => {
    setEditBedrooms([...editBedrooms, createDefaultBedroom()]);
  };

  const removeEditBedroom = (index) => {
    if (editBedrooms.length > 1) {
      setEditBedrooms(editBedrooms.filter((_, i) => i !== index));
    }
  };

  const updateEditBedCount = (bedroomIndex, bedType, delta) => {
    setEditBedrooms(editBedrooms.map((bedroom, i) => {
      if (i === bedroomIndex) {
        const newCount = Math.max(0, (bedroom.beds[bedType] || 0) + delta);
        return { ...bedroom, beds: { ...bedroom.beds, [bedType]: newCount } };
      }
      return bedroom;
    }));
  };

  const handleDelete = async () => {
    try {
      await deleteProperty(deleteId);
      toast.success('Property deleted');
      setDeleteId(null);
      loadProperties();
    } catch (error) {
      toast.error('Failed to delete property');
    }
  };

  const openEditModal = (property) => {
    setEditingProperty(property);
    setEditName(property.name);
    setEditAddress(property.address || '');
    setEditCity(property.city || '');
    setEditAvgCleaningTime(property.averageCleaningTime ?? '');
    setEditBedrooms(property.bedrooms && property.bedrooms.length > 0 
      ? property.bedrooms 
      : [createDefaultBedroom()]);
    setEditCheckoutInstructions(normalizeInstructions(property.checkoutInstructions));
  };

  const closeEditModal = () => {
    setEditingProperty(null);
    setEditName('');
    setEditAddress('');
    setEditBedrooms([]);
  };

  const handleSaveEdit = async () => {
    if (!editName.trim()) {
      toast.error('Property name is required');
      return;
    }

    setSaving(true);
    try {
      await updateProperty(editingProperty.id, { 
        name: editName, 
        address: editAddress || null,
        city: editCity || null,
        averageCleaningTime: editAvgCleaningTime === '' ? null : Number(editAvgCleaningTime),
        bedrooms: editBedrooms,
        checkoutInstructions: editCheckoutInstructions
      });
      toast.success('Property updated successfully');
      closeEditModal();
      loadProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update property');
    } finally {
      setSaving(false);
    }
  };

  const handleManualCheckout = async (property) => {
    setCheckingOutId(property.id);
    try {
      const res = await manualCheckout(property.id);
      if (res.data.ok) {
        toast.success(`Checkout registered for ${property.name}`);
      } else if (res.data.status === 'rate_limited') {
        toast.error('Too many checkouts recently. Please wait a few minutes.');
      } else {
        toast.error('Could not register checkout');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not register checkout');
    } finally {
      setCheckingOutId(null);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-lg text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <PullToRefresh onRefresh={async () => { await Promise.all([loadProperties(), loadMaintenanceMeta()]); }}>
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
      <MobileNav />

      <main className="flex-1 px-4 pt-4 pb-28 md:pb-8 max-w-5xl mx-auto w-full">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold gradient-text">
              {isAdmin ? 'Properties' : 'My Properties'}
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {isAdmin ? 'Manage your QR checkout codes' : 'Properties assigned to you'}
            </p>
          </div>
          {(isAdmin || (isManager && maintenanceEnabled)) && (
            <div className="flex items-center gap-2">
              {maintenanceEnabled && (
                <button
                  onClick={() => setShowMaintenance(true)}
                  className="relative h-11 px-4 bg-white border border-amber-200 hover:bg-amber-50 text-amber-600 rounded-xl font-semibold text-sm flex items-center gap-2 transition-all active:scale-95 shadow-sm"
                  data-testid="open-maintenance-button"
                  title="Maintenance"
                >
                  <Wrench size={18} />
                  <span className="hidden sm:inline">Maintenance</span>
                  {maintenanceOpen > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-amber-500 text-white text-[11px] font-bold flex items-center justify-center ring-2 ring-white" data-testid="maintenance-badge">
                      {maintenanceOpen}
                    </span>
                  )}
                </button>
              )}
              {isAdmin && (
                <>
                  <button
                    onClick={() => setShowImportModal(true)}
                    className="h-11 px-4 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-xl font-semibold text-sm flex items-center gap-2 transition-all active:scale-95 shadow-sm"
                    data-testid="open-import-button"
                  >
                    <Upload size={18} />
                    <span className="hidden sm:inline">Import CSV</span>
                  </button>
                  <button
                    onClick={() => setShowCreateForm(!showCreateForm)}
                    className="h-11 px-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-semibold text-sm flex items-center gap-2 transition-all active:scale-95 shadow-sm"
                    data-testid="toggle-create-button"
                  >
                    <Plus size={18} />
                    <span className="hidden sm:inline">New Property</span>
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Search Bar */}
        {properties.length > 0 && (
          <div className="mb-6 animate-slide-in" style={{ animationDelay: '0.05s' }}>
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                <Search size={20} className="text-slate-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name or address..."
                className="w-full h-14 bg-white border-2 border-slate-200 rounded-2xl transition-all duration-200 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 text-slate-800 placeholder:text-slate-400"
                style={{ paddingLeft: '48px', paddingRight: searchQuery ? '48px' : '18px' }}
                data-testid="search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X size={18} />
                </button>
              )}
            </div>
            {searchQuery && (
              <p className="text-sm text-slate-500 mt-2 ml-1">
                Found {filteredProperties.length} of {properties.length} properties
              </p>
            )}
          </div>
        )}

        {/* Filters */}
        {properties.length > 0 && (cities.length > 0 || isAdmin) && (
          <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="property-filters">
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={`inline-flex items-center gap-2 h-11 px-4 rounded-xl border-2 text-sm font-semibold transition-colors ${
                    activeFilterCount > 0
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                  }`}
                  data-testid="filters-button"
                >
                  <SlidersHorizontal size={16} />
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="ml-0.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-gradient-to-r from-blue-500 to-emerald-500 text-white text-xs font-bold" data-testid="filters-active-count">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-80 p-0 rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-xl">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-white">
                  <h4 className="text-base font-bold text-slate-900">Filters</h4>
                  {activeFilterCount > 0 && (
                    <span className="text-xs font-medium text-slate-400">{activeFilterCount} active</span>
                  )}
                </div>
                <div className="p-5 space-y-4">
                  {cities.length > 0 && (
                    <div>
                      <label className="flex items-center gap-1.5 text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5"><MapPin size={13} /> City</label>
                      <Select value={cityFilter} onValueChange={setCityFilter}>
                        <SelectTrigger className="w-full h-11 rounded-xl" data-testid="city-filter-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="all">All</SelectItem>
                          {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {isAdmin && staffOptions.length > 0 && (
                    <div>
                      <label className="flex items-center gap-1.5 text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5"><UserIcon size={13} /> Staff</label>
                      <Select value={staffFilter} onValueChange={setStaffFilter}>
                        <SelectTrigger className="w-full h-11 rounded-xl" data-testid="staff-filter-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="all">All</SelectItem>
                          {staffOptions.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email.split('@')[0]}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {isAdmin && (
                    <div>
                      <label className="flex items-center gap-1.5 text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5"><Filter size={13} /> Status</label>
                      <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-full h-11 rounded-xl" data-testid="status-filter-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <div className="px-5 py-3 border-t border-slate-100 bg-slate-50">
                  <button
                    type="button"
                    onClick={resetFilters}
                    disabled={activeFilterCount === 0}
                    className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-xl text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40 disabled:hover:bg-transparent"
                    data-testid="reset-filters-button"
                  >
                    <RotateCcw size={15} /> Reset filters
                  </button>
                </div>
              </PopoverContent>
            </Popover>

            {cityFilter !== 'all' && (
              <ActiveFilterPill label={cityFilter} onRemove={() => setCityFilter('all')} testid="active-filter-city" />
            )}
            {staffFilter !== 'all' && (
              <ActiveFilterPill label={staffOptions.find((u) => u.id === staffFilter)?.name || 'Staff'} onRemove={() => setStaffFilter('all')} testid="active-filter-staff" />
            )}
            {statusFilter !== 'all' && (
              <ActiveFilterPill label={statusFilter === 'active' ? 'Active' : 'Inactive'} onRemove={() => setStatusFilter('all')} testid="active-filter-status" />
            )}

            {(searchQuery || activeFilterCount > 0) && (
              <span className="text-sm font-medium text-slate-400 ml-auto" data-testid="filter-result-count">{filteredProperties.length} of {properties.length}</span>
            )}
          </div>
        )}

        {isAdmin && showCreateForm && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-3d p-6 mb-8"
          >
            <h3 className="text-2xl font-bold gradient-text mb-6">Create New Property</h3>
            <form onSubmit={handleCreateProperty} className="space-y-4" data-testid="create-property-form">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Property Name *</label>
                <input
                  type="text"
                  value={newPropertyName}
                  onChange={(e) => setNewPropertyName(e.target.value)}
                  placeholder="e.g., Ocean View Suite"
                  maxLength={35}
                  data-testid="property-name-input"
                  className="input-3d w-full h-14"
                  required
                />
                <div className="flex justify-end mt-1">
                  <span className={`text-xs font-medium ${newPropertyName.length >= 35 ? 'text-red-500' : newPropertyName.length >= 30 ? 'text-amber-500' : 'text-slate-400'}`} data-testid="name-counter-create">
                    {newPropertyName.length}/35
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Address</label>
                <input
                  type="text"
                  value={newPropertyAddress}
                  onChange={(e) => setNewPropertyAddress(e.target.value)}
                  placeholder="e.g., 123 Beach Road, Miami FL 33139"
                  data-testid="property-address-input"
                  className="input-3d w-full h-14"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">City</label>
                <input
                  type="text"
                  value={newPropertyCity}
                  onChange={(e) => setNewPropertyCity(e.target.value)}
                  placeholder="e.g., London"
                  data-testid="property-city-input"
                  className="input-3d w-full h-14"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Average cleaning time</label>
                <select
                  value={newAvgCleaningTime}
                  onChange={(e) => setNewAvgCleaningTime(e.target.value)}
                  data-testid="property-cleaning-time-input"
                  className="input-3d w-full h-14"
                >
                  {CLEANING_TIME_OPTIONS.map((o) => (
                    <option key={o.label} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-400 mt-1.5">Optional. If set, staff get a reminder when a cleaning goes over this time without being marked Ready.</p>
              </div>
              
              {/* Bedrooms Configuration */}
              <div className="pt-4">
                <div className="flex items-center justify-between mb-4">
                  <label className="text-sm font-semibold text-slate-700">Bedrooms</label>
                  <button
                    type="button"
                    onClick={addBedroom}
                    className="text-sm text-emerald-600 hover:text-emerald-700 font-semibold flex items-center gap-1"
                  >
                    <Plus size={16} /> Add Bedroom
                  </button>
                </div>
                
                <div className="space-y-4">
                  {bedrooms.map((bedroom, bedroomIndex) => (
                    <div key={bedroomIndex} className="bg-slate-50 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-semibold text-slate-800">Bedroom {bedroomIndex + 1}</span>
                        {bedrooms.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeBedroom(bedroomIndex)}
                            className="text-red-500 hover:text-red-600 text-sm"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {BED_TYPES.map((bedType) => (
                          <div key={bedType} className="flex items-center justify-between bg-white rounded-lg p-2 border border-slate-200">
                            <div className="flex items-center gap-1.5">
                              <Bed size={14} className="text-slate-400" />
                              <span className="text-xs text-slate-700">{bedType}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => updateBedCount(bedroomIndex, bedType, -1)}
                                className="w-5 h-5 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600"
                              >
                                <Minus size={12} />
                              </button>
                              <span className="w-4 text-center text-xs font-medium">{bedroom.beds[bedType] || 0}</span>
                              <button
                                type="button"
                                onClick={() => updateBedCount(bedroomIndex, bedType, 1)}
                                className="w-5 h-5 rounded-full bg-emerald-100 hover:bg-emerald-200 flex items-center justify-center text-emerald-600"
                              >
                                <Plus size={12} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Checkout Instructions (optional) */}
              <div>
                <label className="text-sm font-semibold text-slate-700">Checkout instructions (optional)</label>
                <p className="text-xs text-slate-500 mb-3">Shown to guests on the checkout page after they scan this property's QR code. Leave empty to show nothing.</p>
                <CheckoutInstructionsPicker
                  selected={checkoutInstructions}
                  onChange={setCheckoutInstructions}
                />
              </div>

              {/* Scheduled tasks (staged until the property is created) */}
              <div className="pt-2 border-t border-slate-100">
                <PropertyTasksEditor value={newTasks} onChange={setNewTasks} locked={!tasksEnabled} />
              </div>

              <div className="flex justify-end pt-2">
                <button
                  type="submit"
                  disabled={creating}
                  className="btn-3d btn-3d-accent h-14 px-8 rounded-2xl font-semibold flex items-center justify-center"
                  data-testid="create-property-button"
                >
                  {creating ? 'Creating...' : 'Create Property'}
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {properties.length === 0 ? (
          <div className="bg-white rounded-3xl p-8 text-center shadow-sm border border-slate-100" data-testid="no-properties">
            <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <PropertiesIcon size={32} className="text-emerald-500" strokeWidth={1.5} />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              {isAdmin ? 'No properties yet' : 'No properties assigned'}
            </h3>
            <p className="text-slate-500 text-sm mb-5">
              {isAdmin 
                ? 'Create your first property to get started'
                : 'Your admin will assign properties to you'
              }
            </p>
            {isAdmin && (
              <button
                onClick={() => setShowCreateForm(true)}
                className="h-11 px-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-semibold text-sm flex items-center gap-2 mx-auto transition-all active:scale-95"
              >
                <Plus size={18} />
                Create Property
              </button>
            )}
          </div>
        ) : filteredProperties.length === 0 ? (
          <div className="bg-white rounded-3xl p-8 text-center shadow-sm border border-slate-100">
            <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Search size={28} className="text-slate-300" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No matching properties</h3>
            <p className="text-slate-500 text-sm mb-4">Try a different search term</p>
            <button
              onClick={() => setSearchQuery('')}
              className="h-10 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold text-sm transition-all"
            >
              Clear Search
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {filteredProperties.map((property, index) => (
              <motion.div
                key={property.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="card-3d property-card-3d p-5 relative overflow-hidden"
                data-testid={`property-card-${property.slug}`}
              >
                <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${property.isActive ? 'bg-gradient-to-b from-emerald-400 to-blue-500' : 'bg-slate-200'}`} />
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{
                      background: property.isActive 
                        ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
                        : 'linear-gradient(135deg, #94A3B8 0%, #64748B 100%)'
                    }}>
                      <PropertiesIcon size={18} className="text-white" strokeWidth={1.8} />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-slate-900">{property.name}</h3>
                      {property.address && (
                        <p className="text-xs text-slate-500">{property.address}</p>
                      )}
                      {property.city && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 mt-0.5" data-testid={`property-city-${property.slug}`}>
                          <MapPin size={11} className="text-slate-400" />
                          {property.city}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
                        property.isActive
                          ? 'bg-green-50 text-green-700 border-green-200'
                          : 'bg-slate-100 text-slate-500 border-slate-200'
                      }`}
                      data-testid={`property-status-${property.slug}`}
                    >
                      {property.isActive ? (
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                        </span>
                      ) : (
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                      )}
                      {property.isActive ? 'Active' : 'Inactive'}
                    </span>
                    <div className="flex items-center gap-1">
                      {isAdmin && (
                        <button
                          onClick={() => openEditModal(property)}
                          className="text-slate-400 hover:text-blue-600 p-1.5 rounded-lg hover:bg-blue-50 transition-colors"
                          title="Edit property"
                          data-testid={`edit-property-${property.slug}`}
                        >
                          <Pencil size={16} />
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          onClick={() => setDeleteId(property.id)}
                          className="text-slate-400 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                          title="Delete property"
                          data-testid={`delete-property-${property.slug}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Assigned Team Members */}
                {(isAdmin || isManager) && (() => {
                  const assignedList = isAdmin
                    ? (property.assignedUsers || [])
                    : (property.assignedUsers || []).filter((u) => myCleanerIds.has(u.id));
                  return (
                  <div className="mb-3">
                    {assignedList.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {[...assignedList].sort((a, b) => {
                          const rank = (u) => (u.role === 'admin' ? 0 : u.cleaningCompanyId ? 1 : 2);
                          return rank(a) - rank(b);
                        }).slice(0, 3).map((member) => (
                          <span
                            key={member.id}
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${
                              member.role === 'admin'
                                ? 'bg-amber-50 text-amber-700 border-amber-300'
                                : member.cleaningCompanyId
                                ? 'bg-gradient-to-r from-purple-50 to-indigo-50 text-purple-700 border-purple-200'
                                : 'bg-blue-50 text-blue-700 border-blue-200'
                            }`}
                            data-testid={`assigned-user-${property.slug}-${member.id}`}
                          >
                            {member.role === 'admin' ? <Shield size={12} /> : <UserIcon size={12} />}
                            {member.name || member.email.split('@')[0]}
                          </span>
                        ))}
                        {assignedList.length > 3 && (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                            +{assignedList.length - 3} more
                          </span>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 italic">{isManager ? 'No cleaner assigned yet' : 'No team members assigned'}</p>
                    )}
                  </div>
                  );
                })()}

                {/* Bedrooms Info */}
                {property.bedrooms && property.bedrooms.length > 0 && (() => {
                  const grandTotal = property.bedrooms.reduce((sum, b) => sum + Object.values(b.beds || {}).reduce((s, c) => s + c, 0), 0);
                  return (
                  <div className="mb-3 rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden" data-testid={`bedrooms-${property.slug}`}>
                    <div className="flex items-center justify-between px-2.5 py-1.5 bg-slate-50">
                      <span className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                        <Bed size={13} className="text-slate-500" />
                        {property.bedrooms.length} Bedroom{property.bedrooms.length > 1 ? 's' : ''}
                      </span>
                      <span className="text-[11px] text-slate-400">{grandTotal} bed{grandTotal !== 1 ? 's' : ''} total</span>
                    </div>
                    {property.bedrooms.map((bedroom, idx) => {
                      const totalBeds = Object.values(bedroom.beds || {}).reduce((sum, count) => sum + count, 0);
                      const bedList = Object.entries(bedroom.beds || {})
                        .filter(([_, count]) => count > 0)
                        .map(([type, count]) => `${count} ${type}`)
                        .join(' \u00B7 ');
                      return totalBeds > 0 ? (
                        <div key={idx} className="flex items-center gap-2 px-2.5 py-1.5">
                          <span className="w-9 h-5 rounded text-[10px] font-extrabold flex items-center justify-center flex-shrink-0 leading-none" style={{
                            background: 'linear-gradient(#fff,#fff) padding-box, linear-gradient(135deg,#3B82F6,#10B981) border-box',
                            border: '1.5px solid transparent',
                            color: '#047857'
                          }}>BR{idx + 1}</span>
                          <span className="text-[11px] text-slate-600">{bedList}</span>
                        </div>
                      ) : null;
                    })}
                  </div>
                  );
                })()}

                {/* Average cleaning time */}
                {property.averageCleaningTime ? (
                  <div className="mb-4 flex items-center gap-2 bg-amber-50 rounded-xl px-3 py-2 border border-amber-100" data-testid={`avg-cleaning-${property.slug}`}>
                    <Clock size={14} className="text-amber-500 flex-shrink-0" />
                    <span className="text-xs font-semibold text-amber-700">Avg cleaning time: {formatCleaningTime(property.averageCleaningTime)}</span>
                    <Bell size={13} className="text-amber-500 ml-auto flex-shrink-0" data-testid={`avg-cleaning-bell-${property.slug}`} title="Reminder active: staff are reminded if this property isn't marked Ready within the average cleaning time" />
                  </div>
                ) : null}

                {/* Footer */}
                <div className="pt-3 border-t border-slate-100">
                  <div className="flex items-center gap-2">
                    {isAdmin && (
                      <button
                        onClick={() => navigate(`/properties/${property.id}`)}
                        className="btn-3d flex-1 h-9 px-3 rounded-xl bg-gradient-to-r from-blue-500 to-emerald-500 text-white text-sm font-semibold flex items-center justify-center gap-1.5 whitespace-nowrap hover:opacity-90 transition-opacity"
                        data-testid={`view-property-${property.slug}`}
                      >
                        <QrCode size={14} />
                        View QR
                      </button>
                    )}
                    <button
                      onClick={() => setConfirmCheckout(property)}
                      disabled={checkingOutId === property.id}
                      title="Manual check-out"
                      className="btn-3d flex-1 h-9 px-3 rounded-xl bg-white border-2 border-red-500 text-red-600 text-sm font-semibold flex items-center justify-center gap-1.5 whitespace-nowrap hover:bg-red-50 transition-colors disabled:opacity-50"
                      data-testid={`checkout-property-${property.slug}`}
                    >
                      <LogOut size={14} />
                      {checkingOutId === property.id ? 'Saving...' : (isAdmin ? 'Checkout' : 'Manual Checkout')}
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      {/* Manual Checkout Confirmation Dialog */}
      <AlertDialog open={!!confirmCheckout} onOpenChange={() => setConfirmCheckout(null)}>
        <AlertDialogContent data-testid="confirm-checkout-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Manual Checkout</AlertDialogTitle>
            <AlertDialogDescription>
              This registers a manual checkout for <strong>{confirmCheckout?.name}</strong>. Only do this if you know the guest has actually left (for example, they forgot to scan the QR code). {isAdmin
                ? 'It lets your assigned staff start the cleaning and marks the property in today\u2019s activity.'
                : 'It lets you start the cleaning and marks the property in today\u2019s activity.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-checkout">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { const p = confirmCheckout; setConfirmCheckout(null); if (p) handleManualCheckout(p); }}
              className="bg-gradient-to-r from-blue-500 to-emerald-500 hover:opacity-90"
              data-testid="confirm-checkout"
            >
              Yes, checked out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Property</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure? This will permanently delete the property and its QR code will stop working.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Property Modal */}
      <AnimatePresence>
        {editingProperty && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={closeEditModal}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-500 to-emerald-500 p-6 text-center">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Pencil size={32} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white">Edit Property</h2>
                <p className="text-white/80 text-sm mt-1">Update details and bedrooms</p>
              </div>

              {/* Form */}
              <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Property Name *
                  </label>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="e.g., Ocean View Suite"
                    maxLength={35}
                    className="input-3d w-full h-14"
                    data-testid="edit-property-name"
                  />
                  <div className="flex justify-end mt-1">
                    <span className={`text-xs font-medium ${editName.length >= 35 ? 'text-red-500' : editName.length >= 30 ? 'text-amber-500' : 'text-slate-400'}`} data-testid="name-counter-edit">
                      {editName.length}/35
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Address
                  </label>
                  <input
                    type="text"
                    value={editAddress}
                    onChange={(e) => setEditAddress(e.target.value)}
                    placeholder="e.g., 123 Beach Road"
                    className="input-3d w-full h-14"
                    data-testid="edit-property-address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">City</label>
                  <input
                    type="text"
                    value={editCity}
                    onChange={(e) => setEditCity(e.target.value)}
                    placeholder="e.g., London"
                    className="input-3d w-full h-14"
                    data-testid="edit-property-city"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Average cleaning time</label>
                  <select
                    value={editAvgCleaningTime}
                    onChange={(e) => setEditAvgCleaningTime(e.target.value)}
                    data-testid="edit-property-cleaning-time"
                    className="input-3d w-full h-14"
                  >
                    {CLEANING_TIME_OPTIONS.map((o) => (
                      <option key={o.label} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-400 mt-1.5">Optional. Staff get a reminder if a cleaning overruns this time.</p>
                </div>

                {/* Bedrooms Configuration */}
                <div className="pt-2">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-semibold text-slate-700">Bedrooms</label>
                    <button
                      type="button"
                      onClick={addEditBedroom}
                      className="text-sm text-emerald-600 hover:text-emerald-700 font-semibold flex items-center gap-1"
                    >
                      <Plus size={16} /> Add Bedroom
                    </button>
                  </div>
                  
                  <div className="space-y-3">
                    {editBedrooms.map((bedroom, bedroomIndex) => (
                      <div key={bedroomIndex} className="bg-slate-50 rounded-xl p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-slate-800 text-sm">Bedroom {bedroomIndex + 1}</span>
                          {editBedrooms.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeEditBedroom(bedroomIndex)}
                              className="text-red-500 hover:text-red-600 text-xs"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {BED_TYPES.map((bedType) => (
                            <div key={bedType} className="flex items-center justify-between bg-white rounded-lg p-2 border border-slate-200">
                              <div className="flex items-center gap-1.5">
                                <Bed size={14} className="text-slate-400" />
                                <span className="text-xs text-slate-700">{bedType}</span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <button
                                  type="button"
                                  onClick={() => updateEditBedCount(bedroomIndex, bedType, -1)}
                                  className="w-5 h-5 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600"
                                >
                                  <Minus size={12} />
                                </button>
                                <span className="w-4 text-center text-xs font-medium">{bedroom.beds?.[bedType] || 0}</span>
                                <button
                                  type="button"
                                  onClick={() => updateEditBedCount(bedroomIndex, bedType, 1)}
                                  className="w-5 h-5 rounded-full bg-emerald-100 hover:bg-emerald-200 flex items-center justify-center text-emerald-600"
                                >
                                  <Plus size={12} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Checkout Instructions (optional) */}
                <div>
                  <label className="text-sm font-semibold text-slate-700">Checkout instructions (optional)</label>
                  <p className="text-xs text-slate-500 mb-3">Shown to guests on the checkout page after scanning the QR.</p>
                  <CheckoutInstructionsPicker
                    selected={editCheckoutInstructions}
                    onChange={setEditCheckoutInstructions}
                  />
                </div>

                <p className="text-xs text-slate-500 italic">
                  Note: The QR code will remain the same.
                </p>

                {/* Scheduled tasks (live) */}
                <div className="pt-2 border-t border-slate-100">
                  <PropertyTasksEditor propertyId={editingProperty.id} locked={!tasksEnabled} />
                </div>
              </div>

              {/* Footer */}
              <div className="p-6 pt-0 flex gap-3">
                <button
                  onClick={closeEditModal}
                  className="flex-1 h-12 rounded-xl font-semibold border-2 border-slate-200 hover:bg-slate-50 text-slate-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={saving || !editName.trim()}
                  className="flex-1 h-12 rounded-xl font-semibold bg-gradient-to-r from-blue-500 to-emerald-500 text-white hover:opacity-90 disabled:opacity-50"
                  data-testid="save-edit-button"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <BulkImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImported={loadProperties}
      />

      <MaintenanceModal
        open={showMaintenance}
        onClose={() => { setShowMaintenance(false); loadMaintenanceMeta(); }}
        properties={properties}
        isAdmin={isAdmin}
        onChange={(openCount) => setMaintenanceOpen(openCount)}
      />
    </div>
    </PullToRefresh>
  );
}
